import React, { ReactNode, useState } from 'react';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { FAQPageJsonLd } from 'next-seo';
import { useRouter } from 'next/router';

import { Button } from '@/components/atoms';
import { PlusMinusIconAnimated } from '@/components/atoms/Icons/plus';
import { DynamicTextWithDecoration } from '@/components/sections/dynamic-blocks/DynamicTextWithDecoration';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { DynamicFaqFragment } from '@/graphql/generated';
import { stripHtml } from '@/utils/drop-html-tags';
import { TextDecorationIcons } from '@/utils/text-decorations';

export type QAItem = {
  header: string;
  text: ReactNode;
  rawAnswerForSchema?: string;
};

export type QASectionProps = {
  header: DynamicFaqFragment['header'] | ReactNode;
  items: QAItem[];
  isDarkMode?: boolean;
  textDecoration?: TextDecorationIcons;
};

export const QASection = ({ header, items, textDecoration, isDarkMode }: QASectionProps) => {
  const router = useRouter();
  const [showAll, setShowAll] = useState(false);
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const initialItemsCount = 5;
  const isHomePage = router.pathname === '/[lang]' || router.pathname === '/';
  const shouldShowSeeMore = isHomePage && items.length > initialItemsCount;
  const displayedItems = shouldShowSeeMore && !showAll ? items.slice(0, initialItemsCount) : items;

  const shouldShowContent = (index: number) => {
    return expandedItems.includes(index) || hoveredItem === index;
  };

  const handleItemClick = (index: number) => {
    if (expandedItems.includes(index)) {
      setExpandedItems(expandedItems.filter((i) => i !== index));
    } else {
      setExpandedItems([...expandedItems, index]);
    }
  };

  return (
    <SectionWrapper>
      <FAQPageJsonLd
        mainEntity={items.map((item) => ({
          questionName: stripHtml(item.header || ''),
          acceptedAnswerText: item.rawAnswerForSchema || (typeof item.text === 'string' ? item.text : ''),
        }))}
      />
      <SectionContentWrapper>
        <Flex
          flexDir="column"
          gap={10}
          maxW={{
            base: '100%',
            md: '792px',
          }}
          justifyContent="center"
          alignItems="center"
          mx="auto"
        >
          <Flex>
            <Heading
              as="h3"
              variant={{
                base: 'component-heading-large/lg',
                sm: 'component-heading-large/xl',
                md: 'text-4xl/lineHeight-44/font-normal',
              }}
              color="text.dark.500"
              whiteSpace="pre-line"
              zIndex={0}
              textAlign="center"
            >
              {/* TODO: here always should come string from CMS but QASection is in pages which aren't moved to CMS yet */}
              {typeof header === 'string' ? (
                <DynamicTextWithDecoration isDarkMode={isDarkMode} {...textDecoration}>
                  {header}
                </DynamicTextWithDecoration>
              ) : (
                header
              )}
            </Heading>
          </Flex>
          <Flex
            flex={{
              base: 1.5,
              lg: 1,
            }}
            mt={-4}
            width="100%"
          >
            <Box width="100%">
              {displayedItems.map((item, index) => (
                <Box
                  key={`faq-item-${index}`}
                  borderBottomWidth="1px"
                  borderBottomColor="divider"
                  pt="28px"
                  pb="28px"
                  px="12px"
                  position="relative"
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  _before={{
                    content: '""',
                    position: 'absolute',
                    top: '4px',
                    left: 0,
                    right: 0,
                    bottom: '4px',
                    bg: 'bg-secondary',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                  _hover={{ _before: { opacity: 1 } }}
                >
                  <Flex
                    width="100%"
                    position="relative"
                    zIndex={1}
                    alignItems="center"
                    justifyContent="space-between"
                    cursor="pointer"
                  >
                    <Heading
                      variant={{
                        base: 'text-lg/lineHeight-7/font-semibold',
                        sm: 'component-article-headline/2xl',
                        md: 'text-lg/lineHeight-8/font-normal',
                      }}
                      color="text-dark.500"
                      textAlign="start"
                      onClick={() => handleItemClick(index)}
                      cursor="pointer"
                      flex="1"
                    >
                      {item.header}
                    </Heading>
                    <Button
                      label=""
                      aria-label="toggle accordion item"
                      leftIcon={<PlusMinusIconAnimated plus={!shouldShowContent(index)} />}
                      variant="sliderLight"
                      iconSpacing={0}
                      minH="48px"
                      minW="48px"
                      ml={2}
                      onClick={() => handleItemClick(index)}
                    />
                  </Flex>

                  {/* Custom content panel */}
                  <Box
                    maxWidth={{
                      base: '100%',
                      md: 'calc(100% - 80px)',
                    }}
                    position="relative"
                    zIndex={1}
                    mt={shouldShowContent(index) ? 4 : 0}
                    maxH={shouldShowContent(index) ? 'none' : 0}
                    overflow="hidden"
                    opacity={shouldShowContent(index) ? 1 : 0}
                    transition="all 0.2s ease-in-out"
                  >
                    <Text as="div" variant="text-md/lineHeight-24/font-normal" color="text-dark.300">
                      {item.text}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </Flex>
          {shouldShowSeeMore && (
            <Flex justify="center" mb={10}>
              <Button
                label={showAll ? 'Show less' : 'Show more'}
                variant="primaryPurple"
                onClick={() => setShowAll(!showAll)}
              />
            </Flex>
          )}
        </Flex>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
